import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import { Progress, UncontrolledCollapse } from 'reactstrap';
import styled from 'styled-components';
import scrollToComponent from 'react-scroll-to-component-ssr';
import Layout from 'components/Layout';
import ProductTitle from 'components/ProductTitle';
import ProductBrandName from 'components/ProductBrandName';
import Link, { LinkRight } from 'components/Link';
import { timeAgo } from 'components/TimeAgo';
import Banner from 'components/Banner';
import media from 'components/Media';
import BoxWhite from 'components/BoxWhite';
import Markup from 'components/Markup';
import PageContainer, { Wrap } from 'components/PageContainer';
import Quote, { AuthorImg } from 'components/Quote';
import { fetchBrandReport } from 'actions/brands';
import NotFound from 'components/NotFound';
import ThemeIcon from 'components/ThemeIcon';
import theme from 'lib/theme';
import i18n from 'i18n';
import prismic from '../prismic';

class Brand extends Component {
  constructor(props) {
    super(props);
    this.top = React.createRef();
  }

  static async getInitialProps({
    store,
    req,
    query: { slug, confirmed_age_max, preview_token },
    lang
  }) {
    const [brand_id] = slug.split('-');
    const result = store.dispatch(
      fetchBrandReport({ brand_id, confirmed_age_max, preview_token, lang })
    );
    return { brand_id, ...result };
  }

  state = {
    enableBackToTop: false
  };

  setVisibility = (isVisible) => this.setState({ enableBackToTop: !isVisible });

  scrollToTop = (e) => {
    e.preventDefault();
    scrollToComponent(this.top.current, {
      offset: 0,
      align: 'top',
      duration: 400
    });
  };

  render() {
    const { brands, brand_id, ...rest } = this.props;
    const { enableBackToTop } = this.state;
    const brand = brands.reports[brand_id];
    if (!brand) return <NotFound {...rest} />;
    const brandName = brand.name;
    const brandNameLong = brand.full_name;

    const meta = {
      title: i18n.t('HOW_SUSTAINABLE_IS_SUBJECT', { subject: brandNameLong }),
      image: brand.logo_url.medium,
      hidden: brand.preview
    };
    const {
      cta,
      related,
      intro_link,
      top_cert_description,
      health_scores_description
    } = prismic.brand(rest.prismic.brand);
    const transparentBrand = i18n.t('BRAND_X_IS_TRANSPARENT_QM', {
      brand: brandName
    });

    const banner = (
      <Banner
        className={classnames('text-white', {
          'bg-flamingo': brand.preview,
          'bg-paradiso': !brand.preview
        })}>
        {brand.preview ? i18n.t('NOTICE_PREVIEW') : i18n.t('NOTICE_PILOT')}{' '}
        {i18n.t('NOTICE_CONTACT')}{' '}
        <a href="mailto:info@thequestionmark.org">info@thequestionmark.org</a>
      </Banner>
    );

    return (
      <Layout
        {...this.props}
        dept="product-checker"
        meta={meta}
        cta={cta}
        related={related}
        banner={banner}>
        <PageContainer>
          <Wrap className="mt-2 mt-sm-5 mx-3 mx-sm-0">
            <div className="d-flex align-items-center" ref={this.top}>
              <div>
                <BrandTitle>{brandNameLong}</BrandTitle>
                {!!brand.full_name_note && (
                  <BrandSubTitle>{brand.full_name_note}</BrandSubTitle>
                )}
              </div>
              {true /* @todo get from API whether brand is transparent */ && (
                <div className="ml-auto text-center">
                  <Link route={intro_link} title={transparentBrand}>
                    <TransparentBrand
                      src="/static/images/icon-transparent-via-questionmark.svg"
                      className="img-fluid mb-2"
                      alt={transparentBrand}
                    />
                  </Link>
                  <div>
                    <Link route={intro_link}>{i18n.t('READ_MORE')}</Link>
                  </div>
                </div>
              )}
            </div>
          </Wrap>

          {/* Product header block */}
          <VisibilitySensor
            onChange={this.setVisibility}
            partialVisibility="top">
            <div className="row my-4 my-sm-5">
              <div className="col-lg-3 mx-3 mx-sm-0 position-relative">
                <BoxWhite className="p-4 rounded">
                  <BrandLogo src={brand.logo_url.medium} alt={brandName} />
                </BoxWhite>
              </div>
              <div className="col-lg-8 mx-3 mx-sm-0 mt-4 mt-lg-0">
                <p>{i18n.t('BRAND_PAGE_INTRO', { brand: brandName })}</p>
                <div className="text-muted mt-2">
                  {i18n.t('INFO_PROVIDED_ON', {
                    date: timeAgo(brand.updated_on)
                  })}
                </div>
              </div>
            </div>
          </VisibilitySensor>

          <BoxWhite className="col-lg-9 offset-lg-2 col-md-12 px-0 text-center">
            {brand.assortment_image_url && (
              <BrandCategoryImage bg={brand.assortment_image_url.large} />
            )}
            <LinkRight
              route={`/search?brand_id=${brand_id}`}
              className="py-3 d-block">
              {i18n.t('VIEW_BRAND_PRODUCTS', {
                count: brand.product_count,
                brand: brandName
              })}
            </LinkRight>
          </BoxWhite>

          {/* Sustainability */}
          {brand.sustainability_policies.length > 0 && (
            <BoxWhite className="p-4 p-sm-5 col-md-12 col-lg-9 my-0 my-sm-5">
              <div className="d-flex align-items-center mb-2">
                <ThemeIcon
                  name="sustainability"
                  style={{ width: 50, height: 45, flexShrink: 0 }}
                />
                <BlockTitle>{i18n.t('SUSTAINABILITY_POLICIES')}</BlockTitle>
              </div>
              <p>
                {i18n.t('SUSTAINABILITY_POLICIES_INTRO', { brand: brandName })}
              </p>
              <ul>
                {brand.sustainability_policies.map((statement, index) => (
                  <li key={index}>
                    <Markup>{statement.body}</Markup>
                  </li>
                ))}
              </ul>
            </BoxWhite>
          )}

          {/* Sustainability indicators */}
          <BoxWhite
            className={classnames(
              'p-4 p-sm-5 col-md-12 col-lg-6 offset-lg-2 my-0 my-sm-5 block-ship',
              brand.sustainability_policies.length > 0 && 'overlap-y-lg'
            )}>
            <div className="container">
              <h4>
                {i18n.t('TOP_CERTIFICATES')}
                <InfoButton id="top-certificates-info-button" />
              </h4>
              <UncontrolledCollapse toggler="#top-certificates-info-button">
                <Markup className="font-weight-light mt-n2" collapsible>
                  {top_cert_description}
                </Markup>
              </UncontrolledCollapse>
              {brand.top_certificates.map(function (cert, index) {
                const pct = (100 * cert.product_count) / brand.product_count;
                const wantSpace =
                  brand.product_without_top_certificate_count > 0 &&
                  index < brand.top_certificates.length;
                return (
                  <div
                    key={cert.id}
                    className={classnames('row', wantSpace ? 'my-3' : 'mt-3')}>
                    {cert.image_url && (
                      <div className="col-sm-3">
                        <img
                          src={cert.image_url.thumb}
                          alt={cert.name}
                          className="img-fluid pr-3"
                          style={{ maxWidth: 80 }}
                        />
                      </div>
                    )}
                    <div className="col-sm-9">
                      <Progress value={pct} className="my-2" color="primary">
                        {pct > 8 && cert.product_count}
                      </Progress>
                      {i18n.t('TOP_CERTIFICATE_PRODUCT_COUNT', {
                        count: cert.product_count,
                        certificate: cert.name
                      })}
                      <br />
                    </div>
                  </div>
                );
              })}
              {brand.product_without_top_certificate_count > 0 && (
                <div className="row mt-3">
                  <Markup>
                    {brand.product_without_top_certificate_count ===
                    brand.product_count
                      ? i18n.t('NO_TOP_CERTIFICATES_ALL_PRODUCTS', {
                          brand: brandName
                        })
                      : i18n.t('NO_TOP_CERTIFICATES_PRODUCT_COUNT', {
                          count: brand.product_without_top_certificate_count,
                          brand: brandName
                        })}
                  </Markup>
                </div>
              )}
            </div>
          </BoxWhite>

          {/* Sustainability ambitions */}
          {brand.sustainability_ambitions.length > 0 && (
            <BoxWhite className="p-4 p-sm-5 offset-lg-3 my-0 my-sm-5 overlap-y-lg block-corn">
              <div className="d-flex align-items-center mb-2">
                <div>
                  <ThemeIcon
                    name="sustainability"
                    style={{ width: 50, height: 45, flexShrink: 0 }}
                  />
                </div>
                <BlockTitle>{i18n.t('SUSTAINABILITY_AMBITIONS')}</BlockTitle>
              </div>
              <p>
                {i18n.t('SUSTAINABILITY_AMBITIONS_INTRO', { brand: brandName })}
              </p>
              <ul>
                {brand.sustainability_ambitions.map((statement, index) => (
                  <li key={index}>
                    <Markup>{statement.body}</Markup>
                  </li>
                ))}
              </ul>
            </BoxWhite>
          )}

          {/* Health */}
          {brand.health_policies.length > 0 && (
            <BoxWhite className="p-4 p-sm-5 col-md-12 col-lg-9 my-0 my-sm-5">
              <div className="d-flex align-items-center mb-2">
                <div>
                  <ThemeIcon name="health" style={{ width: 45, height: 50 }} />
                </div>
                <BlockTitle>{i18n.t('HEALTH_POLICIES')}</BlockTitle>
              </div>
              <p>{i18n.t('HEALTH_POLICIES_INTRO', { brand: brandName })}</p>
              <ul>
                {brand.health_policies.map((statement, index) => (
                  <li key={index}>
                    <Markup>{statement.body}</Markup>
                  </li>
                ))}
              </ul>
            </BoxWhite>
          )}

          {/* Health indicators */}
          {brand.health_scores.length > 0 && (
            <BoxWhite
              className={classnames(
                'p-4 p-sm-5 col-md-12 offset-lg-4 col-lg-7 my-0 my-sm-5 block-ship',
                brand.health_policies.length > 0 && 'overlap-y-lg'
              )}>
              <div className="container mt-2">
                <h4>
                  {i18n.t('HEALTH_SCORES')}
                  <InfoButton id="health-scores-info-button" />
                </h4>
                <UncontrolledCollapse toggler="#health-scores-info-button">
                  <Markup className="mb-2 font-weight-light mt-n2" collapsible>
                    {health_scores_description}
                  </Markup>
                </UncontrolledCollapse>
                {brand.health_scores.map(function (group, index) {
                  // ignore unscored products (with +null+ label)
                  const data = group.histogram.data.filter(
                    ([label, count]) => label !== null && count > 0
                  );
                  const total_count = data
                    .map(([_, count]) => count)
                    .reduce((r, c) => r + c);
                  const isLast = index === brand.health_scores.length - 1;
                  const showBar = data.length > 1;
                  return (
                    <div
                      key={index}
                      className={classnames('row', isLast || 'mb-4')}>
                      {showBar && (
                        <div className="col-sm-12 mt-2">
                          <DistributionBar
                            total={total_count}
                            data={data.map(([label, count]) => ({
                              key: label,
                              label: count,
                              count,
                              color: theme.colors.health_scores[label]
                            }))}
                          />
                        </div>
                      )}
                      {showBar && (
                        <div className="col-sm-12 mt-1">
                          <DistributionBar
                            transparent
                            total={total_count}
                            data={data.map(([label, count]) => ({
                              key: label,
                              label: i18n.t(`HEALTH_${label.toUpperCase()}`),
                              count,
                              color: theme.colors.health_scores[label]
                            }))}
                          />
                        </div>
                      )}
                      <div className="col-sm-12 mt-2">
                        <HealthScoresText
                          product_count={brand.product_count}
                          score_count={total_count}
                          data={data}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </BoxWhite>
          )}

          {/* Health ambitions */}
          {brand.health_ambitions.length > 0 && (
            <BoxWhite
              className={classnames(
                'p-4 p-sm-5 col-md-12 col-lg-9 offset-lg-1 my-0 my-sm-5 block-corn',
                brand.health_scores.length > 0 && 'overlap-y-lg'
              )}>
              <div className="d-flex align-items-center mb-2">
                <div>
                  <ThemeIcon name="health" style={{ width: 45, height: 50 }} />
                </div>
                <BlockTitle>{i18n.t('HEALTH_AMBITIONS')}</BlockTitle>
              </div>
              <p>{i18n.t('HEALTH_AMBITIONS_INTRO', { brand: brandName })}</p>
              <ul>
                {brand.health_ambitions.map((statement, index) => (
                  <li key={index}>
                    <Markup>{statement.body}</Markup>
                  </li>
                ))}
              </ul>
            </BoxWhite>
          )}

          {/* Initiatives */}
          {brand.initiatives.length > 0 && (
            <BoxWhite className="p-4 p-sm-5 offset-lg-2 col-lg-9 my-0 my-sm-5 block-ship">
              <h2>{i18n.t('INITIATIVES', { brand: brandName })}</h2>
              {brand.initiatives.map(function (item, index) {
                const isLast = index === brand.initiatives.length - 1;
                return (
                  <div
                    className={classnames(
                      isLast ? 'mt-3 mt-sm-3' : 'my-3 my-sm-5',
                      'row no-gutters'
                    )}
                    key={item.id}>
                    <div className="col-2">
                      {item.logo_url && (
                        <img
                          src={item.logo_url.thumb}
                          className="img-fluid pr-4 pt-2"
                        />
                      )}
                    </div>
                    <div className="col-10">
                      <div>{item.name}</div>
                      {item.description && (
                        <div className="font-weight-light">
                          {item.description}
                        </div>
                      )}
                      {item.website_url && (
                        <LinkRight route={item.website_url}>
                          {i18n.t('INITIATIVE_READ_MORE')}
                        </LinkRight>
                      )}
                    </div>
                  </div>
                );
              })}
            </BoxWhite>
          )}

          {/* Quotes */}
          {brand.brand_quotes.slice(0, 1).map((quote) => (
            <BoxWhite
              className="p-4 p-sm-5 col-lg-9 my-0 my-sm-5"
              key={quote.body}>
              <p>{quote.author}</p>
              <Quote color="lightGrey" className="my-0 ml-0 ml-sm-2">
                <Markup>{quote.body}</Markup>
                <footer>
                  <div className="d-flex align-items-center">
                    {quote.author_headshot_url && (
                      <AuthorImg
                        right
                        color="white"
                        className="img-fluid mr-2 rounded-circle"
                        src={quote.author_headshot_url.medium}
                        alt=""
                      />
                    )}
                  </div>
                </footer>
              </Quote>
              {brand.website_url && (
                <Link className="btn btn-light mt-3" route={brand.website_url}>
                  <i className="fas fa-link" />
                  &nbsp;{i18n.t('WEBSITE')}
                </Link>
              )}{' '}
              {brand.csr_report_url && (
                <Link
                  className="btn btn-light mt-3"
                  route={brand.csr_report_url}>
                  <i className="fas fa-leaf" />
                  &nbsp;{i18n.t('CSR_REPORT')}
                </Link>
              )}{' '}
              {brand.contact_email && (
                <Link
                  className="btn btn-light mt-3"
                  route={`mailto:${brand.contact_email}`}>
                  <i className="far fa-envelope" />
                  &nbsp;{i18n.t('SEND_AN_EMAIL')}
                </Link>
              )}
            </BoxWhite>
          ))}

          {/* Source */}
          <BoxWhite className="p-4 p-sm-5 offset-lg-1 col-lg-9 block-ship my-0 my-sm-5">
            <h2>{i18n.t('SOURCE')}</h2>
            <Markup>{brand.source_description}</Markup>
          </BoxWhite>

          {enableBackToTop && (
            <BackToTop onClick={this.scrollToTop} href="">
              Back to top
            </BackToTop>
          )}
        </PageContainer>
      </Layout>
    );
  }
}

Brand.propTypes = {
  brands: PropTypes.object,
  prismic: PropTypes.object,
  brand_id: PropTypes.string
};

export default connect(({ brands, prismic }) => ({
  brands,
  prismic
}))(Brand);

const TransparentBrand = styled.img`
  width: 80px;
  ${media.down.tablet`
    width: 60px;
  `};
`;

const BrandLogo = styled.img.attrs({ className: 'img-fluid mx-auto d-block' })`
  max-width: 150px;
  ${media.up.desktop`
    max-width: 125px;
  `};
`;

const BackToTop = styled.a.attrs({
  className: 'btn btn-outline-primary bg-white'
})`
  position: fixed;
  bottom: 0;
  right: 0;
  margin: 15px;
  ${media.up.desktop`
    display: none;
  `};
`;

const BrandCategoryImage = styled.div`
  height: 350px;
  width: 100%;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background: url(${(props) => props.bg}) top center no-repeat;
  background-size: cover;
  ${media.down.phone`
    height: 150px;
    border-radius: 0;
  `};
`;

const BrandTitle = styled(ProductTitle)`
  ${media.down.phone`
    font-size: 1.6rem;
    font-weight: bold;
  `};
`;

const BrandSubTitle = styled(ProductBrandName)`
  font-size: 1.4rem;
  ${media.down.phone`
    font-size: 1rem;
  `};
`;

const InfoButton = ({ id }) => (
  <small style={{ fontSize: '1.2rem', paddingLeft: 10 }}>
    <a href="#" id={id}>
      <i className="fa fa-info-circle" />
    </a>
  </small>
);

InfoButton.propTypes = {
  id: PropTypes.string.isRequired
};

const DistributionBar = ({ total, data, transparent }) => (
  <div className="progress" style={{ background: !!transparent && 'none' }}>
    {data.map(function ({ label, count, color, key }) {
      const pct = (100 * count) / total;
      return (
        <div
          key={key}
          className="progress-bar"
          role="progressbar"
          style={{
            width: `${pct}%`,
            color: !!transparent && color,
            background: transparent ? 'none' : color,
            whiteSpace: 'nowrap'
          }}
          aria-valuenow={pct}>
          {pct > 8 && label}
        </div>
      );
    })}
  </div>
);

DistributionBar.defaultProps = {
  transparent: false
};

DistributionBar.propTypes = {
  total: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  transparent: PropTypes.bool.isRequired
};

const HealthScoresText = ({ product_count, score_count, data, ...props }) => (
  <div {...props}>
    <Markup component="span">
      {health_scores_main_html({ product_count, score_count, data })}
    </Markup>
    {product_count > score_count && (
      <span className="text-muted">
        {' '}
        {i18n.t('PRODUCTS_WITHOUT_SCORE', {
          count: product_count - score_count
        })}
        {'.'}
      </span>
    )}
    {product_count === score_count && '.'}
  </div>
);

HealthScoresText.propTypes = {
  product_count: PropTypes.number.isRequired,
  score_count: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired
};

function health_scores_main_html({ product_count, score_count, data }) {
  if (!data.length) return null;

  const [score, count] = data.find(([score, count]) => count > 0);
  if (!score) return null;

  const iprefix = `PRODUCTS_${score === 'A' ? 'IN' : 'OUTSIDE'}_SCHIJF`;

  const scoreColor = theme.colors.health_scores[score];
  const scoreText = (i18n.t(`HEALTH_${score}`) || '?').toLowerCase();
  const scoreHtml = `<span style="color: ${scoreColor}">${scoreText}</span>`;

  const args = { count, total: score_count, score: scoreHtml };

  // If all products have this score, return "All products ..."
  if (product_count === count) {
    return i18n.t(iprefix + '_ALL', args);
  }

  // If all scored products have this score, return "All x products ..."
  if (score_count === count) {
    return i18n.t(iprefix + '_ALL_X', args);
  }

  // Otherwise, "x of y products ..."
  return i18n.t(iprefix, args);
}

const BlockTitle = styled.h2`
  ${media.down.tablet`
    margin-bottom: 0;
  `};
`;
